// ============================================================================
// RowActionsMenu — pt.89 (2026-09-21).
// Overflow (⋮) menu on Pipeline rows/cards. Full action set:
//   - View detail            (any pipeline role)
//   - Advance to next stage  (any pipeline role, ≥10-char reason)
//   - Reassign owner         (any pipeline role, grouped user picker + reason)
//   - Escalate / flag        (any pipeline role, ≥10-char reason; toggles to
//                             Un-flag when candidate.flagged is already true)
//   - Archive candidate      (any pipeline role, reason ∈ {Rejected|Withdrew|
//                             Duplicate|Test data|Other})
//   - Unarchive              (Archived tab only)
//   - Delete permanently     (platform_admin only)
// ============================================================================
import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";
import {
  MoreVertical, Archive, Undo2, Trash2, Loader2,
  Eye, ArrowRightCircle, UserCog, Flag, FlagOff, UserX,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { stageLabel } from "./stageTitles";

const ARCHIVE_REASONS = ["Rejected", "Withdrew", "Duplicate", "Test data", "Other"];
const STAGE_LABELS = Object.fromEntries([1, 2, 3, 4, 5].map((n) => [n, stageLabel(n)]));

export default function RowActionsMenu({ candidate, isArchivedView, isPlatformAdmin, onChanged }) {
  // pt.89 shipped a hand-rolled absolute-positioned popover. That
  // popover was clipped by the pipeline table's `overflow-hidden`
  // wrapper on prod — the menu opened, but you couldn't see it (bug
  // CC-041, 2026-09-24). Migrated to Radix DropdownMenu which renders
  // into a portal, so it can never be clipped by an ancestor's
  // overflow. All data-testids preserved.
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [notQualifiedOpen, setNotQualifiedOpen] = useState(false);
  const { slug } = useParams();

  async function doUnarchive() {
    try {
      await api.post(`/hiring/candidates/${candidate.id}/unarchive`, {});
      toast.success(`${candidate.contact?.name || "Candidate"} restored to Stage ${candidate.archived_from_stage || "?"}`);
      onChanged?.();
    } catch (e) {
      toast.error(extractApiError(e)?.message || "Unarchive failed");
    }
  }

  async function doUnflag() {
    try {
      await api.post(`/hiring/candidates/${candidate.id}/unflag`, {});
      toast.success(`Flag cleared on ${candidate.contact?.name || "candidate"}`);
      onChanged?.();
    } catch (e) {
      toast.error(extractApiError(e)?.message || "Unflag failed");
    }
  }

  const detailHref = `/${slug}/app/hiring/candidates/${candidate.id}`;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); }}
            className="p-1 rounded hover:bg-[#f3f4f6] text-[#4b5563] outline-none focus:ring-2 focus:ring-[#93c5fd]"
            data-testid={`hiring-pipeline-row-actions-${candidate.id}`}
            title="Actions"
            aria-label="Row actions"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={4}
          className="w-56 bg-white"
          data-testid={`hiring-pipeline-row-actions-menu-${candidate.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {!isArchivedView && (
            <>
              <DropdownMenuItem asChild>
                <Link
                  to={detailHref}
                  className="flex items-center gap-2 text-[#0e1420] cursor-pointer"
                  data-testid={`hiring-pipeline-view-${candidate.id}`}
                >
                  <Eye className="w-3.5 h-3.5" /> View candidate detail
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setAdvanceOpen(true)}
                disabled={candidate.stage >= 5}
                className="flex items-center gap-2 text-[#0e1420] cursor-pointer"
                data-testid={`hiring-pipeline-advance-${candidate.id}`}
              >
                <ArrowRightCircle className="w-3.5 h-3.5" /> Advance to next stage
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setReassignOpen(true)}
                className="flex items-center gap-2 text-[#0e1420] cursor-pointer"
                data-testid={`hiring-pipeline-reassign-${candidate.id}`}
              >
                <UserCog className="w-3.5 h-3.5" /> Reassign owner
              </DropdownMenuItem>
              {candidate.flagged ? (
                <DropdownMenuItem
                  onSelect={() => doUnflag()}
                  className="flex items-center gap-2 text-[#8a5a1a] cursor-pointer"
                  data-testid={`hiring-pipeline-unflag-${candidate.id}`}
                >
                  <FlagOff className="w-3.5 h-3.5" /> Clear flag
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onSelect={() => setFlagOpen(true)}
                  className="flex items-center gap-2 text-[#0e1420] cursor-pointer"
                  data-testid={`hiring-pipeline-flag-${candidate.id}`}
                >
                  <Flag className="w-3.5 h-3.5" /> Escalate / flag
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setNotQualifiedOpen(true)}
                className="flex items-center gap-2 text-[#7a1c1c] focus:bg-[#fef2f2] focus:text-[#7a1c1c] cursor-pointer"
                data-testid={`hiring-pipeline-not-qualified-${candidate.id}`}
              >
                <UserX className="w-3.5 h-3.5" /> Mark as Not Qualified
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setArchiveOpen(true)}
                className="flex items-center gap-2 text-[#0e1420] cursor-pointer"
                data-testid={`hiring-pipeline-archive-${candidate.id}`}
              >
                <Archive className="w-3.5 h-3.5" /> Archive candidate
              </DropdownMenuItem>
            </>
          )}
          {isArchivedView && (
            <DropdownMenuItem
              onSelect={() => doUnarchive()}
              className="flex items-center gap-2 text-[#0e1420] cursor-pointer"
              data-testid={`hiring-pipeline-unarchive-${candidate.id}`}
            >
              <Undo2 className="w-3.5 h-3.5" /> Unarchive
            </DropdownMenuItem>
          )}
          {isPlatformAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setDeleteOpen(true)}
                className="flex items-center gap-2 text-[#7a1c1c] focus:bg-[#fee2e2] focus:text-[#7a1c1c] cursor-pointer"
                data-testid={`hiring-pipeline-delete-${candidate.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete permanently
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ArchiveDialog open={archiveOpen} onOpenChange={setArchiveOpen} candidate={candidate} onDone={onChanged} />
      <NotQualifiedDialog open={notQualifiedOpen} onOpenChange={setNotQualifiedOpen} candidate={candidate} onDone={onChanged} />
      <DeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} candidate={candidate} onDone={onChanged} />
      <AdvanceDialog open={advanceOpen} onOpenChange={setAdvanceOpen} candidate={candidate} onDone={onChanged} />
      <ReassignDialog open={reassignOpen} onOpenChange={setReassignOpen} candidate={candidate} onDone={onChanged} />
      <FlagDialog open={flagOpen} onOpenChange={setFlagOpen} candidate={candidate} onDone={onChanged} />
    </>
  );
}

// ─────────────────────────────────────────────────────────── Advance stage
function AdvanceDialog({ open, onOpenChange, candidate, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const nextStage = Math.min(5, (candidate.stage || 1) + 1);
  const canSubmit = reason.trim().length >= 10;

  async function submit() {
    if (!canSubmit || busy) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(
        `/hiring/candidates/${candidate.id}/advance-stage`,
        { reason: reason.trim() },
      );
      toast.success(`Advanced to ${STAGE_LABELS[data.new_stage] || `Stage ${data.new_stage}`}`);
      onOpenChange(false); setReason("");
      onDone?.();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Advance failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hiring-pipeline-advance-dialog">
        <DialogHeader>
          <DialogTitle>Advance {candidate.contact?.name || "candidate"} to next stage?</DialogTitle>
          <DialogDescription>
            Moving from <b>{STAGE_LABELS[candidate.stage] || `Stage ${candidate.stage}`}</b> → <b>{STAGE_LABELS[nextStage]}</b>. The new stage owner(s) will be emailed the full context.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">
              Reason (min 10 chars, required)
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Passed hard-gate screen; interviewer approved"
              data-testid="hiring-pipeline-advance-reason"
            />
          </div>
          {err && (
            <div className="text-[12px] text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid="hiring-pipeline-advance-error">
              {err}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hiring-pipeline-advance-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !canSubmit} data-testid="hiring-pipeline-advance-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Advancing…</> : `Advance to ${STAGE_LABELS[nextStage] || `Stage ${nextStage}`}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────── Reassign owner
function ReassignDialog({ open, onOpenChange, candidate, onDone }) {
  const [users, setUsers] = useState([]);
  const [stageOwners, setStageOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOwner, setNewOwner] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const [usersRes, settingsRes] = await Promise.all([
          api.get("/users"),
          api.get("/hiring/settings").catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setUsers((usersRes.data || []).filter((u) => u.active !== false));
        // Pull stage owners from HiringSettings.stages[currentStage].primary_owner_emails
        const stageIdx = (candidate.stage || 1) - 1;
        const roles = settingsRes.data?.stages || [];
        const owners = roles[stageIdx]?.primary_owner_emails || roles[stageIdx]?.owner_emails || [];
        setStageOwners(owners.map((e) => e.toLowerCase()));
      } catch (e) {
        setErr(extractApiError(e)?.message || "Failed to load user list");
      } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, candidate.stage]);

  const grouped = useMemo(() => {
    const stageSet = new Set(stageOwners);
    const stageGroup = users.filter((u) => stageSet.has((u.email || "").toLowerCase()));
    const otherGroup = users.filter((u) => !stageSet.has((u.email || "").toLowerCase()));
    return { stageGroup, otherGroup };
  }, [users, stageOwners]);

  const canSubmit = newOwner && reason.trim().length >= 10;

  async function submit() {
    if (!canSubmit || busy) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(
        `/hiring/candidates/${candidate.id}/reassign-owner`,
        { new_owner_email: newOwner, reason: reason.trim() },
      );
      toast.success(`Reassigned to ${data.new_owner_email}`);
      onOpenChange(false); setNewOwner(""); setReason("");
      onDone?.();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Reassign failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hiring-pipeline-reassign-dialog">
        <DialogHeader>
          <DialogTitle>Reassign owner for {candidate.contact?.name || "candidate"}</DialogTitle>
          <DialogDescription>
            Currently owned by: <b data-testid="hiring-pipeline-reassign-current-owner">{
              (candidate.current_owner_display && candidate.current_owner_display.length
                ? candidate.current_owner_display.map((o) => o.name && o.name !== o.email ? `${o.name} (${o.email})` : o.email).join(", ")
                : (candidate.current_owner_emails || []).join(", ")) || "(unassigned)"
            }</b>.
            The new owner will be emailed the full candidate context and reason.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">
              New owner
            </label>
            <select
              value={newOwner}
              onChange={(e) => setNewOwner(e.target.value)}
              disabled={loading}
              className="w-full h-10 rounded-md border border-[#e5e7eb] bg-white px-3 text-sm"
              data-testid="hiring-pipeline-reassign-select"
            >
              <option value="">{loading ? "Loading users…" : "Select a user…"}</option>
              {grouped.stageGroup.length > 0 && (
                <optgroup label={`Stage ${candidate.stage} owners`}>
                  {grouped.stageGroup.map((u) => (
                    <option key={u.email} value={u.email}>
                      {u.name || u.email} — {u.email} ({u.role})
                    </option>
                  ))}
                </optgroup>
              )}
              {grouped.otherGroup.length > 0 && (
                <optgroup label="All other active users">
                  {grouped.otherGroup.map((u) => (
                    <option key={u.email} value={u.email}>
                      {u.name || u.email} — {u.email} ({u.role})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">
              Reason (min 10 chars, required)
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Original owner OOO — Missie taking over"
              data-testid="hiring-pipeline-reassign-reason"
            />
          </div>
          {err && (
            <div className="text-[12px] text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid="hiring-pipeline-reassign-error">
              {err}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hiring-pipeline-reassign-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !canSubmit} data-testid="hiring-pipeline-reassign-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reassigning…</> : "Reassign owner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────── Flag / escalate
function FlagDialog({ open, onOpenChange, candidate, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const canSubmit = reason.trim().length >= 10;

  async function submit() {
    if (!canSubmit || busy) return;
    setBusy(true); setErr(null);
    try {
      await api.post(
        `/hiring/candidates/${candidate.id}/flag`,
        { reason: reason.trim() },
      );
      toast.success(`Flagged for escalation — owner(s) + escalation contacts notified`);
      onOpenChange(false); setReason("");
      onDone?.();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Flag failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hiring-pipeline-flag-dialog">
        <DialogHeader>
          <DialogTitle>Escalate / flag {candidate.contact?.name || "candidate"}?</DialogTitle>
          <DialogDescription>
            Flags a candidate for the current owner(s), CCs the tenant's escalation contacts (or brett@brettcpayne.com as fallback), and puts a visible flag badge on the row until manually cleared.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">
              Reason (min 10 chars, required)
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Owner has been silent for 5 days — need executive push"
              data-testid="hiring-pipeline-flag-reason"
            />
          </div>
          {err && (
            <div className="text-[12px] text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid="hiring-pipeline-flag-error">
              {err}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hiring-pipeline-flag-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !canSubmit} className="bg-[#c96a1a] hover:bg-[#a55214]" data-testid="hiring-pipeline-flag-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Flagging…</> : "Flag & notify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────── Archive (existing)
function ArchiveDialog({ open, onOpenChange, candidate, onDone }) {
  const [reason, setReason] = useState("Rejected");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const needsNotes = reason === "Other";

  async function submit() {
    if (needsNotes && !notes.trim()) { setErr("Please add a short note explaining why."); return; }
    setBusy(true); setErr(null);
    try {
      await api.post(`/hiring/candidates/${candidate.id}/archive`, {
        reason, notes: notes.trim() || undefined,
      });
      toast.success(`${candidate.contact?.name || "Candidate"} archived (${reason})`);
      onOpenChange(false); setReason("Rejected"); setNotes("");
      onDone?.();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Archive failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hiring-pipeline-archive-dialog">
        <DialogHeader>
          <DialogTitle>Archive {candidate.contact?.name || "candidate"}?</DialogTitle>
          <DialogDescription>
            Archive is preferred over delete — archived candidates are removed from active view but their full history stays for audit. You can unarchive at any time from the Archived tab.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">Reason</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-10 rounded-md border border-[#e5e7eb] bg-white px-3 text-sm" data-testid="hiring-pipeline-archive-reason">
              {ARCHIVE_REASONS.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          </div>
          {needsNotes && (
            <div>
              <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">Notes (required for &quot;Other&quot;)</label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. moved out of service area" data-testid="hiring-pipeline-archive-notes" />
            </div>
          )}
          {err && (
            <div className="text-[12px] text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid="hiring-pipeline-archive-error">{err}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hiring-pipeline-archive-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="hiring-pipeline-archive-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Archiving…</> : "Archive candidate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────── Mark as Not Qualified (CC-045)
function NotQualifiedDialog({ open, onOpenChange, candidate, onDone }) {
  // CC-045 (2026-09-24) — Uses the existing `/archive` endpoint with
  // reason locked to "Not qualified". Backend routes that reason to
  // `hiring_notifications.dispatch_not_qualified_candidate_notice`
  // which fires a warm decline email to the CANDIDATE'S contact
  // email (never surfaces the reason text). All the audit stamping
  // Archive already does (archived_at, archived_by,
  // archived_from_stage, ownership_history) is inherited unchanged.
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const candidateEmail = candidate.contact?.email || "";
  const canSubmit = reason.trim().length >= 15 && candidateEmail;

  async function submit() {
    if (!canSubmit || busy) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/hiring/candidates/${candidate.id}/archive`, {
        reason: "Not qualified",
        notes: reason.trim(),
      });
      const notif = data?.candidate_notification || {};
      const notifMode = notif.mode;
      if (notifMode === "skipped_no_candidate_email") {
        toast.warning(`${candidate.contact?.name || "Candidate"} marked Not Qualified — no email on file, candidate not notified.`);
      } else if (notifMode === "send_failed") {
        toast.warning(`${candidate.contact?.name || "Candidate"} marked Not Qualified — candidate email failed to send; check logs.`);
      } else if (notifMode === "dry_run") {
        toast.success(`${candidate.contact?.name || "Candidate"} marked Not Qualified — candidate notice queued (dry-run: escalation disabled).`);
      } else {
        toast.success(`${candidate.contact?.name || "Candidate"} marked Not Qualified — decline email sent to candidate.`);
      }
      onOpenChange(false); setReason("");
      onDone?.();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Mark as Not Qualified failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hiring-pipeline-not-qualified-dialog">
        <DialogHeader>
          <DialogTitle>Mark {candidate.contact?.name || "candidate"} as Not Qualified?</DialogTitle>
          <DialogDescription>
            <b>This sends a warm decline email to the candidate</b> at <code className="font-mono text-[#0e1420]">{candidateEmail || "(no email on file)"}</code>.
            The candidate is moved to the Archived tab and can be restored by an admin via Unarchive. Your reason is stored for internal audit and is <b>never</b> shown to the candidate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">
              Reason (min 15 chars, required — internal only)
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Failed hard-gate on unauthorized discounting; also no HVAC background"
              data-testid="hiring-pipeline-not-qualified-reason"
            />
          </div>
          {!candidateEmail && (
            <div className="text-[12px] text-[#8a5a1a] bg-[#fef3d9] border border-[#e6c99a] rounded p-2">
              This candidate has no email on file, so no decline notice will be sent. Cancel and add an email first if you want the candidate notified.
            </div>
          )}
          {err && (
            <div className="text-[12px] text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid="hiring-pipeline-not-qualified-error">
              {err}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hiring-pipeline-not-qualified-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !canSubmit} className="bg-[#7a1c1c] hover:bg-[#5c1414]" data-testid="hiring-pipeline-not-qualified-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Mark & notify candidate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────── Delete (platform_admin)
function DeleteDialog({ open, onOpenChange, candidate, onDone }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const canDelete = typed.trim() === "DELETE";

  async function submit() {
    if (!canDelete) return;
    setBusy(true); setErr(null);
    try {
      await api.delete(`/hiring/candidates/${candidate.id}`);
      toast.success(`${candidate.contact?.name || "Candidate"} deleted permanently`);
      onOpenChange(false); setTyped("");
      onDone?.();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Delete failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hiring-pipeline-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete {candidate.contact?.name || "candidate"} permanently?</DialogTitle>
          <DialogDescription>
            This permanently deletes the candidate&apos;s record and audit trail. It cannot be undone. <b>Archive is almost always the better choice.</b>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-[12px] text-[#0e1420] mb-1">
              Type <code className="font-mono bg-[#f3f4f6] px-1 rounded">DELETE</code> to confirm:
            </label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" data-testid="hiring-pipeline-delete-typed" />
          </div>
          {err && (<div className="text-[12px] text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid="hiring-pipeline-delete-error">{err}</div>)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hiring-pipeline-delete-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !canDelete} className="bg-[#7a1c1c] hover:bg-[#5c1414] disabled:opacity-50" data-testid="hiring-pipeline-delete-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</> : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
